import React, { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { supabase } from "../../services/supabaseClient";
import "maplibre-gl/dist/maplibre-gl.css";

const STYLE = "https://tiles.openfreemap.org/styles/liberty";
const valid = point => Number.isFinite(Number(point?.latitude)) && Number.isFinite(Number(point?.longitude)) && point?.latitude != null && point?.longitude != null;
const coords = point => [Number(point.longitude), Number(point.latitude)];

export default function PrivateRideRoutePreview({ pickup, stops = [], destination, expanded = false }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const latestRouteRef = useRef({ type: "FeatureCollection", features: [] });
  const requestSequenceRef = useRef(0);
  const [roadRoute, setRoadRoute] = useState(null);
  const [routeStatus, setRouteStatus] = useState("idle");
  const [retryKey, setRetryKey] = useState(0);
  const locations = useMemo(() => [pickup, ...stops, destination].filter(valid), [pickup, stops, destination]);
  const signature = locations.map(point => coords(point).join(",")).join(";");
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const frame = window.requestAnimationFrame(() => map.resize());
    const timeout = window.setTimeout(() => map.resize(), 350);
    return () => { window.cancelAnimationFrame(frame); window.clearTimeout(timeout); };
  }, [expanded]);
  useEffect(() => {
    const points = [pickup, ...stops, destination].filter(valid);
    if (points.length < 2 || !valid(pickup) || !valid(destination) || stops.some(stop => !valid(stop))) {
      requestSequenceRef.current += 1;
      setRoadRoute(null);
      setRouteStatus("idle");
      return undefined;
    }
    let cancelled = false;
    const requestNumber = ++requestSequenceRef.current;
    setRoadRoute(null);
    setRouteStatus("loading");
    const timer = window.setTimeout(async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const { data, error } = await supabase.functions.invoke("private-ride-route-v2", {
            body: { action: "preview", points: points.map(point => ({ latitude: Number(point.latitude), longitude: Number(point.longitude) })) }
          });
          if (error || !data?.ok || data.geometry?.type !== "LineString" || !Array.isArray(data.geometry.coordinates) || data.geometry.coordinates.length < 2) {
            throw new Error(data?.message || error?.message || "Road routing unavailable");
          }
          if (cancelled || requestSequenceRef.current !== requestNumber) return;
          setRoadRoute({
            geometry: data.geometry,
            distance: Number(data.distance_km) * 1000,
            duration: Number(data.duration_minutes) * 60,
            signature: points.map(p => coords(p).join(",")).join(";")
          });
          setRouteStatus("ready");
          return;
        } catch (error) {
          if (cancelled || requestSequenceRef.current !== requestNumber) return;
          if (attempt === 1) {
            console.warn("Private Ride road route unavailable:", error);
            setRoadRoute(null);
            setRouteStatus("error");
          }
        }
      }
    }, 320);
    return () => { cancelled = true; requestSequenceRef.current += 1; window.clearTimeout(timer); };
    // Route request is keyed by coordinate signature; changed point objects do not require a fresh request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, retryKey]);
  const displayedRoute = roadRoute?.signature === signature ? roadRoute : null;
  latestRouteRef.current = {
    type: "FeatureCollection",
    features: displayedRoute?.geometry?.type === "LineString"
      ? [{ type: "Feature", geometry: displayedRoute.geometry, properties: {} }]
      : []
  };
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE,
      center: valid(pickup) ? coords(pickup) : [18.86, -33.93],
      zoom: 11,
      attributionControl: false
    });
    mapRef.current = map;
    // Install route source and high-contrast line whenever MapLibre loads
    // its style, including when geometry is already available.
    const ensureRoadLine = () => {
      try {
        if (!map.getStyle()) return;
        if (!map.getSource("ride-overview")) {
          map.addSource("ride-overview", { type: "geojson", data: latestRouteRef.current });
        }
        if (!map.getLayer("ride-overview-outline")) {
          map.addLayer({
            id: "ride-overview-outline", type: "line", source: "ride-overview",
            layout: { "line-cap": "round", "line-join": "round" },
            paint: { "line-color": "#ffffff", "line-width": 9, "line-opacity": 0.95 }
          });
        }
        if (!map.getLayer("ride-overview-line")) {
          map.addLayer({
            id: "ride-overview-line", type: "line", source: "ride-overview",
            layout: { "line-cap": "round", "line-join": "round" },
            paint: { "line-color": "#720e1e", "line-width": 5, "line-opacity": 1 }
          });
        }
        map.getSource("ride-overview")?.setData(latestRouteRef.current);
      } catch (error) {
        console.warn("UniAir route map could not install road line:", error);
      }
    };
    map.on("load", ensureRoadLine);
    map.on("style.load", ensureRoadLine);
    if (map.isStyleLoaded()) ensureRoadLine();
    return () => {
      map.off("load", ensureRoadLine);
      map.off("style.load", ensureRoadLine);
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    const points = [pickup, ...stops, destination].filter(valid);
    points.forEach((point, index) => {
      const marker = document.createElement("div");
      const isPickup = index === 0;
      const isDestination = index === points.length - 1 && valid(destination);
      marker.style.cssText = isPickup
        ? "height:16px;width:16px;border-radius:50%;background:white;border:4px solid #111827;box-shadow:0 2px 7px #0004;"
        : isDestination
          ? "height:20px;width:20px;border-radius:5px;background:#720e1e;border:3px solid white;box-shadow:0 2px 7px #0004;"
          : "height:22px;width:22px;border-radius:50%;background:#374151;border:2px solid white;box-shadow:0 2px 7px #0004;display:grid;place-items:center;color:white;font:bold 11px sans-serif;";
      marker.textContent = isPickup || isDestination ? "" : String(index);
      markersRef.current.push(new maplibregl.Marker({ element: marker }).setLngLat(coords(point)).addTo(map));
    });
    // Map style handler replays latest route if request finishes first.
    map.getSource("ride-overview")?.setData(latestRouteRef.current);

    if (points.length > 1) {
      const bounds = new maplibregl.LngLatBounds(coords(points[0]), coords(points[0]));
      points.slice(1).forEach(point => bounds.extend(coords(point)));
      map.fitBounds(bounds, { padding: 48, maxZoom: 14, duration: 350 });
    } else if (points.length === 1) map.flyTo({ center: coords(points[0]), zoom: 13, duration: 350 });
  }, [signature, pickup, stops, destination, roadRoute]);
  return <div className="relative h-full w-full">
    <div ref={containerRef} className="h-full w-full" aria-label="Map showing your selected pickup, stops and destination" role="img" />
    <div className="pointer-events-auto absolute bottom-2 left-2 rounded-lg bg-white/95 px-2.5 py-1.5 text-[11px] font-semibold text-gray-800 shadow-sm">
      {displayedRoute && routeStatus === "ready"
        ? `${(displayedRoute.distance / 1000).toFixed(1)} km · about ${Math.round(displayedRoute.duration / 60)} min`
        : routeStatus === "loading" ? "Finding your road route…"
        : routeStatus === "error" ? <span>Road route unavailable. <button type="button" onClick={() => setRetryKey(value => value + 1)} className="font-bold text-[#720e1e] underline">Retry</button></span>
        : "Your route"}
    </div>
    <details className="group absolute bottom-2 right-2 z-10 text-[10px] text-gray-600">
      <summary aria-label="Map information and credits" className="cursor-pointer list-none rounded-full bg-white/95 px-2 py-1.5 font-medium shadow-sm [&::-webkit-details-marker]:hidden">
        © OpenStreetMap <span aria-hidden="true" className="ml-0.5">ⓘ</span>
      </summary>
      <div className="absolute bottom-full right-0 mb-2 w-56 rounded-xl bg-white p-3 leading-5 shadow-lg">
        Map data © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" className="underline">OpenStreetMap contributors</a>.
        Map tiles by <a href="https://openfreemap.org" target="_blank" rel="noreferrer" className="underline">OpenFreeMap</a> and <a href="https://openmaptiles.org" target="_blank" rel="noreferrer" className="underline">OpenMapTiles</a>.
      </div>
    </details>
  </div>;
}
